import React, { useEffect, useState, memo } from 'react'
import { useTranslation } from 'react-i18next'
import { useAppDispatch, useAppSelector } from 'app/hooks'
import {
  useLiveReportSavingInfo,
  useIsLiveReportReady,
  useLiveReportInfo
} from 'hooks/useLiveReportConfigModalState'
import { useAccountId } from 'hooks/usePageConfig'
import {
  getLiveReportId,
  getLiveReportInfo,
  updateLiveReportStatus
} from 'features/liveReportConfig/asyncThunks'
import { getSuggestedUserList } from 'features/suggestedUsers/asyncThunks'
import { useForm, FormProvider, SubmitHandler } from 'react-hook-form'
import {
  ToggleButton,
  Dialog,
  Tooltip,
  QuestionMarkOutlined,
  InfoSolid,
  Warning
  // @ts-ignore
} from '@netbasequid/core'
// @ts-ignore
import { sys } from '@netbasequid/theme'
import { useDisplayTheme } from 'hooks/useUiState'
import { AutomationId } from 'utils/automationUtil'
import { useDashboardData } from 'hooks/useDashboardState'
import { WIDGET_MAP } from 'components/widget/WidgetFactory'
import ErrorCode from 'enums/ErrorCode'
import {
  resetSavingInfo,
  closeLiveReportConfigModal
} from 'features/liveReportConfig/liveReportConfigSlice'
import SavingButton from 'components/visualization/SavingButton'
import {
  StyledFormContainer,
  StyledForm,
  StyledLabel,
  StyledActionsContainer,
  StyledReportKeyLabel,
  StyledSubscribersLabel,
  StyledReminderContainer,
  StyledReminder,
  StyledReminderIconContainer,
  StyledLimitedDashboardReminder
} from './index.css'
import ReportKey from './ReportKey'
import Subscribers from './Subscribers'
import ReportLink from './ReportLink'
import { LiveReportConfigForm } from './types'
import WarningDialog from './WarningDialog'
import useRetrieveLiveReportConfig from './hooks/useRetrieveLiveReportConfig'

const LiveReportConfigModal = ({ dashboardId }: { dashboardId: number }) => {
  useRetrieveLiveReportConfig(dashboardId)
  const theme = useDisplayTheme()
  const dispatch = useAppDispatch()
  const dashboardData = useDashboardData()
  const savingInfo = useLiveReportSavingInfo()
  const isLiveReportReady = useIsLiveReportReady()
  const liveReportInfo = useLiveReportInfo()
  const accountId = useAccountId()
  const { t } = useTranslation()
  const [showConfigArea, setShowConfigArea] = useState(false)
  const [showProtectionArea, setShowProtectionArea] = useState(false)
  const [openWarningDialog, setOpenWarningDialog] = useState(false)
  const [showConfigAreaInitialized, setShowConfigAreaInitialized] = useState(false)
  const [showProtectionAreaInitialized, setShowProtectionAreaInitialized] = useState(false)
  const hasLegacyWidgets = dashboardData?.widgets?.some((item) => !WIDGET_MAP[item.type])

  const methods = useForm<LiveReportConfigForm>()

  const isOpen = useAppSelector(({ liveReportConfig }) => liveReportConfig.isOpen)

  const onSubmit: SubmitHandler<LiveReportConfigForm> = (data: LiveReportConfigForm) => {
    const isEnabled = showConfigArea
    const isAnonymous = showConfigArea && !showProtectionArea

    dispatch(
      updateLiveReportStatus({
        dashboardId,
        liveReportInfo: {
          liveReportEnabled: isEnabled,
          anonymous: isAnonymous,
          liveReportId: isEnabled ? data.reportId : '',
          liveReportKey: isAnonymous || !isEnabled ? '' : btoa(data.reportKey),
          liveReportSubscribers: isAnonymous || !isEnabled ? [] : data.subscribersList
        },
        accountId: accountId!
      })
    ).then((resultAction) => {
      if (updateLiveReportStatus.fulfilled.match(resultAction)) {
        dispatch(closeLiveReportConfigModal())
        setShowConfigAreaInitialized(false)
        setShowProtectionAreaInitialized(false)
        setShowConfigArea(false)
        setShowProtectionArea(false)
      }
    })
  }

  const onClose = () => {
    dispatch(closeLiveReportConfigModal())
    // reset to the initial state since we don't want to keep the unsaved changes
    setShowConfigArea(liveReportInfo.reportEnabled)
    setShowProtectionArea(liveReportInfo.reportEnabled && !liveReportInfo.anonymous)
    setShowConfigAreaInitialized(false)
    setShowProtectionAreaInitialized(false)
  }

  const onWarningDialogClose = () => {
    if (savingInfo.statusCode === ErrorCode.PERMISSION_DENIED_INSUFFICIENT_LIVE_REPORT_QUOTA) {
      onClose()
    }
    if (savingInfo.statusCode === ErrorCode.ADMIN_USER_NOT_FOUND) {
      onClose()
      dispatch(getLiveReportId({ dashboardId }))
      dispatch(getLiveReportInfo({ dashboardId }))
      dispatch(getSuggestedUserList(accountId!))
    }
    setOpenWarningDialog(false)
    dispatch(resetSavingInfo())
  }

  useEffect(() => {
    if (liveReportInfo.status === 'succeeded' && !showConfigAreaInitialized) {
      setShowConfigArea(liveReportInfo.reportEnabled || showConfigArea)
      setShowConfigAreaInitialized(true)
    }
  }, [liveReportInfo, showConfigArea, showConfigAreaInitialized])

  useEffect(() => {
    if (liveReportInfo.status === 'succeeded' && !showProtectionAreaInitialized) {
      setShowProtectionArea(
        (liveReportInfo.reportEnabled && !liveReportInfo.anonymous) || showProtectionArea
      )
      setShowProtectionAreaInitialized(true)
    }
  }, [liveReportInfo, showProtectionArea, showProtectionAreaInitialized])

  useEffect(() => {
    setOpenWarningDialog(!!savingInfo.statusCode)
  }, [savingInfo])

  const disableSaveButton = () => {
    if (!isLiveReportReady) {
      return true
    }
    if (savingInfo.saveStatus === 'loading') {
      return true
    }
    if (showProtectionArea) {
      if (!showConfigArea) {
        return false
      }
      if (!methods.formState.isValid) {
        return true
      }
    }
    return false
  }

  return (
    <Dialog
      mode={theme}
      open={isOpen}
      onClose={onClose}
      width="760px"
      maxHeight="85vh"
      isStretchedDivider>
      <Dialog.DialogTitle>{t('liveReport')}</Dialog.DialogTitle>
      <Dialog.DialogContent>
        <WarningDialog
          open={openWarningDialog}
          onClose={onWarningDialogClose}
          statusCode={savingInfo.statusCode}
          users={savingInfo.failedSubscribers}
        />
        <StyledFormContainer
          disable={savingInfo.saveStatus === 'loading'}
          paddings={showConfigArea ? undefined : '16px 0 0 0'}>
          {/* @ts-ignore */}
          <FormProvider
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...methods}>
            <StyledForm>
              <StyledLabel data-testid={AutomationId.liveReportModal.label.generateReport}>
                {t('generateReport')}
              </StyledLabel>
              <StyledActionsContainer
                data-testid={AutomationId.liveReportModal.generateReportToggleContainer}>
                <ToggleButton
                  mode={theme}
                  checked={showConfigArea}
                  onChange={() => {
                    setShowConfigArea(!showConfigArea)
                  }}
                />
              </StyledActionsContainer>

              {showConfigArea && (
                <>
                  <StyledLabel data-testid={AutomationId.liveReportModal.label.reportLink}>
                    {t('reportLink')}
                  </StyledLabel>
                  <ReportLink />
                </>
              )}

              {showConfigArea && (
                <>
                  <StyledLabel data-testid={AutomationId.liveReportModal.label.enableProtection}>
                    {t('enableProtection')}
                  </StyledLabel>
                  <StyledActionsContainer
                    data-testid={AutomationId.liveReportModal.enableProtectionToggleContainer}>
                    <ToggleButton
                      mode={theme}
                      checked={showProtectionArea}
                      onChange={() => {
                        setShowProtectionArea(!showProtectionArea)
                      }}
                    />
                  </StyledActionsContainer>
                </>
              )}

              {showProtectionArea && showConfigArea && (
                <>
                  <StyledReportKeyLabel data-testid={AutomationId.liveReportModal.label.reportKey}>
                    {t('reportKey')}
                    <Tooltip mode={theme} content={t('reportKeyTooltip')}>
                      <QuestionMarkOutlined color={sys[theme].text.placeholder} />
                    </Tooltip>
                  </StyledReportKeyLabel>
                  <ReportKey />

                  <StyledSubscribersLabel
                    data-testid={AutomationId.liveReportModal.label.subscribers}>
                    {t('subscriber_other')}
                  </StyledSubscribersLabel>
                  <Subscribers />
                </>
              )}
            </StyledForm>
          </FormProvider>
        </StyledFormContainer>
        {showConfigArea && (
          <StyledReminderContainer disable={savingInfo.saveStatus === 'loading'}>
            <StyledReminder
              mode={theme}
              data-testid={AutomationId.liveReportModal.authorizedReminder}>
              <StyledReminderIconContainer>
                <InfoSolid fontSize={14} color={sys[theme].common.info} />
              </StyledReminderIconContainer>
              {t('liveReportAuthorizedReminder')}
              <Tooltip mode={theme} content={t('liveReportAuthorizedReminderTooltip')}>
                <QuestionMarkOutlined fontSize={14} color={sys[theme].text.placeholder} />
              </Tooltip>
            </StyledReminder>
            {hasLegacyWidgets && (
              <StyledLimitedDashboardReminder
                data-testid={AutomationId.liveReportModal.limitedDashboardReminder}
                mode={theme}>
                <StyledReminderIconContainer>
                  <Warning fontSize={14} color={sys[theme].semantic.middle.default} />
                </StyledReminderIconContainer>
                {t('liveReportLimitedDashboardReminder')}
              </StyledLimitedDashboardReminder>
            )}
          </StyledReminderContainer>
        )}
      </Dialog.DialogContent>
      <Dialog.DialogActions
        closeButtonProps={{
          onClick: onClose,
          disabled: savingInfo.saveStatus === 'loading',
          children: t('cancel')
        }}
        okButtonProps={{
          children: savingInfo.saveStatus === 'loading' ? <SavingButton /> : <>{t('save')}</>,
          onClick: methods.handleSubmit(onSubmit),
          disabled: disableSaveButton()
        }}
      />
    </Dialog>
  )
}

export default memo(LiveReportConfigModal)
